import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { applyFavouriteToTeamMembers } from "./applyFavouriteToTeamMembers";
import { createFavouriteUsersQueryKey } from "./selectFavouriteUsers";
import type { TeamMembersData } from "./selectTeamMembers";

interface FavouriteUserRow {
    favourited_by: string;
    favourited_user: string;
}

export interface FavouriteCacheSnapshot {
    teamMembers: Array<[QueryKey, TeamMembersData | undefined]>;
    favouriteUsers: FavouriteUserRow[] | undefined;
}

export function applyFavouriteToFavouriteUsers(
    rows: FavouriteUserRow[] | undefined,
    authId: string,
    userId: string,
    isFavourite: boolean
): FavouriteUserRow[] | undefined {
    if (!rows) return rows;
    if (isFavourite) {
        if (rows.some((row) => row.favourited_user === userId)) return rows;
        return [...rows, { favourited_by: authId, favourited_user: userId }];
    }
    return rows.filter((row) => row.favourited_user !== userId);
}

export async function snapshotFavouriteCaches(
    queryClient: QueryClient,
    authId: string
): Promise<FavouriteCacheSnapshot> {
    await Promise.all([
        queryClient.cancelQueries({ queryKey: ["team-members"] }),
        queryClient.cancelQueries({ queryKey: createFavouriteUsersQueryKey(authId) }),
    ]);
    return {
        teamMembers: queryClient.getQueriesData<TeamMembersData>({ queryKey: ["team-members"] }),
        favouriteUsers: queryClient.getQueryData<FavouriteUserRow[]>(createFavouriteUsersQueryKey(authId)),
    };
}

export function applyFavouriteCaches(
    queryClient: QueryClient,
    authId: string,
    userId: string,
    isFavourite: boolean
): void {
    queryClient.setQueriesData<TeamMembersData>({ queryKey: ["team-members"] }, (current) =>
        current ? applyFavouriteToTeamMembers(current, userId, isFavourite) : current
    );
    queryClient.setQueryData(createFavouriteUsersQueryKey(authId), (current: FavouriteUserRow[] | undefined) =>
        applyFavouriteToFavouriteUsers(current, authId, userId, isFavourite)
    );
}

export function restoreFavouriteCaches(
    queryClient: QueryClient,
    authId: string,
    snapshot: FavouriteCacheSnapshot
): void {
    for (const [queryKey, data] of snapshot.teamMembers) {
        queryClient.setQueryData(queryKey, data);
    }
    queryClient.setQueryData(createFavouriteUsersQueryKey(authId), snapshot.favouriteUsers);
}

export function invalidateFavouriteCaches(queryClient: QueryClient, authId: string): void {
    void queryClient.invalidateQueries({ queryKey: createFavouriteUsersQueryKey(authId) });
    void queryClient.invalidateQueries({ queryKey: ["team-members"] });
}

export function isFavouriteUniqueViolation(error: unknown): boolean {
    if (!error || typeof error !== "object") return false;
    const code = "code" in error ? String(error.code) : "";
    const message = "message" in error ? String(error.message) : "";
    return code === "23505" || message.includes("favourite_profiles_pkey");
}
